import { RotateCcw, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  apiClient,
  type BrandOut,
  type BulkEdit,
  type BulkEditFailure,
  type BulkTarget
} from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { Toast } from "@/shared/components/Toast";
import { usePolling } from "@/shared/hooks/usePolling";
import { toMention, type Mention } from "@/shared/types/mention";
import { formatInteger } from "@/shared/utils/format";
import { BulkEditBanner, isBulkEditRunning } from "./BulkEditBanner";
import {
  TRASH_WINDOW_DAYS,
  daysLeftInTrash,
  discardedByLabel,
  trashExitLabel,
  trashListQuery,
  trashRowTitle
} from "./trash-view";
import { Select } from "@/shared/components/Select";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

type ToastValue = { message: string; undo?: () => void };

/**
 * A lixeira de menções (2026-10-18): as descartadas há menos de 15 dias,
 * com restaurar uma a uma ou em lote.
 *
 * Falha de AÇÃO (restaurar, desfazer) vira mensagem sem trocar a lista;
 * falha de CARREGAMENTO com dados na tela mantém a lista e avisa em cima.
 * Restaurar não é destrutivo, então não pede confirmação.
 */
export function TrashPage({
  token,
  companyId,
  openMention,
  mentionChange
}: {
  token: string;
  companyId: string;
  openMention: (mention: Mention) => void;
  mentionChange: number;
}) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [brandId, setBrandId] = useState<number | null>(null);
  const [brands, setBrands] = useState<BrandOut[]>([]);
  const [items, setItems] = useState<Mention[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastValue | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeBulk, setActiveBulk] = useState<BulkEdit | null>(null);
  const [bulkFailures, setBulkFailures] = useState<BulkEditFailure[] | null>(null);
  const [bulkPausedUntil, setBulkPausedUntil] = useState<number | null>(null);
  // Descarte por CARGA: a resposta de uma consulta antiga não vence a atual.
  const loadSeq = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const brandIds = brandId === null ? [] : [brandId];

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    try {
      const result = await apiClient.listMentions(
        token,
        trashListQuery({
          page,
          size: PAGE_SIZE,
          q,
          brandIds: brandId === null ? [] : [brandId]
        }),
        { signal: abort.signal }
      );
      if (seq !== loadSeq.current) return;
      setItems(result.items.map(toMention));
      setTotal(result.total);
      setError(null);
    } catch (failure) {
      if (seq !== loadSeq.current || abort.signal.aborted) return;
      setError(errorMessage(failure, "Não foi possível carregar a lixeira."));
    }
  }, [token, page, q, brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => controller.current?.abort(), []);

  // As marcas do filtro: uma vez ao abrir; falha vira só "Todas".
  useEffect(() => {
    let alive = true;
    apiClient
      .brands(token)
      .then(list => {
        if (alive) setBrands(list.filter(brand => brand.is_active));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [token]);

  // Busca com debounce; trocar o texto volta pra página 1 e limpa a seleção.
  useEffect(() => {
    if (query === q) return;
    const timer = window.setTimeout(() => {
      setQ(query);
      setPage(1);
      setSelected(new Set());
      setSelectAll(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, q]);

  // O drawer mudou uma menção: a lista se refaz. Pula a montagem.
  const seenMentionChange = useRef(mentionChange);
  useEffect(() => {
    if (seenMentionChange.current === mentionChange) return;
    seenMentionChange.current = mentionChange;
    void load();
  }, [mentionChange, load]);

  // O lote em andamento: `getBulkEdit` até terminar, e a lista recarrega.
  const refreshBulk = useCallback(async () => {
    if (!activeBulk) return;
    try {
      const next = await apiClient.getBulkEdit(token, activeBulk.id);
      setActiveBulk(next);
      if (!isBulkEditRunning(next)) void load();
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setBulkPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
    }
  }, [token, activeBulk, load]);

  usePolling({
    enabled: activeBulk !== null && isBulkEditRunning(activeBulk),
    onTick: () => void refreshBulk(),
    signature: activeBulk
      ? `${activeBulk.id}:${activeBulk.status}:${activeBulk.counters.pending}`
      : undefined,
    pausedUntil: bulkPausedUntil
  });

  const clearSelection = () => {
    setSelected(new Set());
    setSelectAll(false);
  };

  const toggle = (id: number) => {
    if (selectAll) {
      // Desmarcar uma no modo "todas" volta pra seleção explícita:
      // a página inteira menos esta.
      setSelectAll(false);
      setSelected(new Set((items ?? []).map(m => m.id).filter(other => other !== id)));
      return;
    }
    setSelected(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const restoreOne = async (mention: Mention) => {
    setActionError(null);
    try {
      await apiClient.restoreMention(token, mention.id);
    } catch (failure) {
      setActionError(errorMessage(failure, "Não foi possível restaurar a menção."));
      return;
    }
    await load();
    setToast({
      message: "Menção restaurada.",
      undo: async () => {
        setToast(null);
        try {
          await apiClient.discardMention(token, mention.id);
        } catch (failure) {
          setActionError(errorMessage(failure, "Não foi possível desfazer."));
          return;
        }
        void load();
      }
    });
  };

  const restoreSelected = async () => {
    const target: BulkTarget = selectAll
      ? { query: trashListQuery({ page: 1, size: PAGE_SIZE, q, brandIds }) }
      : { mentionIds: [...selected], scope: "all" };
    setBusy(true);
    setActionError(null);
    try {
      const result = await apiClient.submitBulkClassification(token, target, {
        discarded: { value: false }
      });
      clearSelection();
      setBulkFailures(null);
      if (isBulkEditRunning(result)) {
        setActiveBulk(result);
      } else {
        setActiveBulk(null);
        await load();
        const n = result.counters.changed;
        setToast({
          message: n === 1 ? "1 menção restaurada." : `${formatInteger(n)} menções restauradas.`
        });
      }
    } catch (failure) {
      setActionError(errorMessage(failure, "Não foi possível restaurar as menções."));
    } finally {
      setBusy(false);
    }
  };

  const showBulkFailures = async () => {
    if (!activeBulk) return;
    try {
      setBulkFailures(await apiClient.listBulkEditFailures(token, activeBulk.id));
    } catch {
      setBulkFailures([]);
    }
  };

  const now = new Date();
  const list = items ?? [];
  const selectedCount = selectAll ? (total ?? 0) : selected.size;
  const isSelected = (id: number) => selectAll || selected.has(id);

  return (
    <div className="uploads-page trash-page">
      <section className="card upload-history-card">
        <div className="card-title">
          <h3>
            <Trash2 size={17} />
            Lixeira{total !== null ? ` · ${formatInteger(total)} na lixeira` : ""}
          </h3>
        </div>
        <p className="downloads-intro">
          Menções descartadas nos últimos {TRASH_WINDOW_DAYS} dias. Depois disso elas continuam
          descartadas — no arquivo, em Mais filtros → “Só as descartadas” da{" "}
          <Link to={`/empresas/${companyId}/mencoes`}>tela de menções</Link>.
        </p>

        <div className="trash-controls">
          <div className="search-field">
            <Search size={17} />
            <input
              name="trash-search"
              autoComplete="off"
              aria-label="Buscar na lixeira"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar na lixeira…"
            />
          </div>
          <Select
            aria-label="Marca"
            value={brandId === null ? "" : String(brandId)}
            onChange={event => {
              setBrandId(event.target.value ? Number(event.target.value) : null);
              setPage(1);
              clearSelection();
            }}
          >
            <option value="">Todas as marcas</option>
            {brands.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </Select>
        </div>

        {activeBulk && (
          <BulkEditBanner
            edit={activeBulk}
            failures={bulkFailures}
            onShowFailures={() => void showBulkFailures()}
            onDismiss={() => {
              setActiveBulk(null);
              setBulkFailures(null);
            }}
          />
        )}

        {error && (
          <ApiState kind="error" onRetry={() => void load()}>
            {error}
          </ApiState>
        )}
        {actionError && (
          <p className="form-error" role="alert">
            {actionError}
          </p>
        )}
        {items === null && !error && <ApiState kind="loading">Carregando…</ApiState>}

        {items !== null && list.length === 0 && (
          <div className="upload-history-empty">
            <Trash2 size={26} />
            <strong>Nada na lixeira nos últimos {TRASH_WINDOW_DAYS} dias.</strong>
          </div>
        )}

        {list.length > 0 && (
          <>
            <div className="trash-bulk">
              {selectedCount > 0 && (
                <>
                  <span>{formatInteger(selectedCount)} selecionada(s)</span>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={busy}
                    onClick={() => void restoreSelected()}
                  >
                    <RotateCcw size={15} />
                    Restaurar selecionadas
                  </button>
                  <button type="button" className="secondary-button" onClick={clearSelection}>
                    Limpar seleção
                  </button>
                </>
              )}
              {!selectAll && total !== null && total > 0 && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setSelectAll(true)}
                >
                  Selecionar todas as {formatInteger(total)} da lixeira
                </button>
              )}
            </div>
            <div className="trash-list">
              {list.map(mention => {
                const days = mention.discardedAt
                  ? daysLeftInTrash(mention.discardedAt, now)
                  : 0;
                const title = trashRowTitle(mention);
                return (
                  <article
                    key={mention.id}
                    className={`trash-row${isSelected(mention.id) ? " is-selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(mention.id)}
                      onChange={() => toggle(mention.id)}
                      aria-label={`Selecionar ${title}`}
                    />
                    <div className="trash-row-body">
                      <span className="trash-row-meta">
                        {mention.brandName}
                        {mention.isCompetitor && (
                          <span className="report-criteria-chip">
                            Concorrente: {mention.companyName}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="trash-row-title"
                        onClick={() => openMention(mention)}
                      >
                        {title}
                      </button>
                      <span className="trash-row-meta">
                        {mention.channel ?? "Sem canal"}
                        {/* `date` vem do `toMention`, pela regra de parede de
                            `published_at` (2026-08-29): os dígitos são o relógio,
                            sem converter fuso — a mesma data da tela de menções.
                            Até 2026-10-25 passava por `parseServerTime`, que é
                            pra carimbo de verdade, e saía com 3 h a menos. */}
                        {` · ${mention.date}`}
                      </span>
                      <span className="trash-row-meta">{discardedByLabel(mention)}</span>
                      <span className="trash-row-meta">{trashExitLabel(days)}</span>
                    </div>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => void restoreOne(mention)}
                      aria-label={`Restaurar ${title}`}
                    >
                      <RotateCcw size={15} />
                      Restaurar
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}

        {total !== null && (
          <Pager page={page} size={PAGE_SIZE} onPage={setPage} count={{ total }} />
        )}
      </section>
      {toast && (
        <Toast
          message={toast.message}
          actionLabel={toast.undo ? "Desfazer" : undefined}
          onAction={toast.undo}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
